import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { TextField, SortMenu, Button, ButtonMenu } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";
import { getUserListFilters } from "../../../../../../../../../services/users";

const Filters = ({
  onSearchChange,
  onOwnerChange,
  onModifiedChange,
  onSortChange,
  onOrderChange,
  selectedSortOption = "name",
  orderDirection = "asc",
  loading = false,
  selectedOwner: externalSelectedOwner = null,
  selectedModified: externalSelectedModified = null,
  searchKeyword = "",
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(searchKeyword);
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedOwner, setSelectedOwner] = useState(
    externalSelectedOwner || []
  );
  const [selectedModified, setSelectedModified] = useState(
    externalSelectedModified || []
  );
  const searchTimeoutRef = useRef(null);
  const [ownerOptions, setOwnerOptions] = useState([]);

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const response = await getUserListFilters({
          page: 1,
          page_size: 50,
          order_by: ["full_name"],
          order_direction: ["ASC"],
        });

        if (response?.body) {
          const options = (response.body || []).map((user) => ({
            value: user.user_uuid || user.userUuid || user.id,
            label: user.full_name || user.fullName || user.name || "",
          }));
          setOwnerOptions(options);
        }
      } catch (error) {
        console.error("Error fetching owner options:", error);
      }
    };

    fetchOwners();
  }, []);

  const modifiedOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This year", value: "this_year" },
  ];

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange?.(value);
      }, 500);
    },
    [onSearchChange]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleOwnerChange = useCallback(
    (value) => {
      const currentValue = selectedOwner?.[0] ?? null;
      const newOwner = currentValue === value ? null : value;
      setSelectedOwner(newOwner ? [newOwner] : []);
      onOwnerChange?.(newOwner ? [newOwner] : null);
    },
    [onOwnerChange, selectedOwner]
  );

  const handleModifiedChange = useCallback(
    (value) => {
      const currentValue = selectedModified?.[0] ?? null;
      const newModified = currentValue === value ? null : value;
      setSelectedModified(newModified ? [newModified] : []);
      onModifiedChange?.(newModified ? [newModified] : null);
    },
    [onModifiedChange, selectedModified]
  );

  const handleClearAllFilters = useCallback(() => {
    setSelectedOwner([]);
    setSelectedModified([]);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onSearchChange?.("");
    onOwnerChange?.(null);
    onModifiedChange?.(null);
  }, [onSearchChange, onOwnerChange, onModifiedChange]);

  const getOwnerLabel = () => {
    const selectedValue = selectedOwner?.[0];
    if (!selectedValue) return "Owner";
    const option = ownerOptions.find((opt) => opt.value === selectedValue);
    return option?.label ?? "Owner";
  };

  const getModifiedLabel = () => {
    const selectedValue = selectedModified?.[0];
    if (!selectedValue) return "Modified";
    const option = modifiedOptions.find((opt) => opt.value === selectedValue);
    return option?.label ?? "Modified";
  };

  useEffect(() => {
    setSearchValue(searchKeyword);
  }, [searchKeyword]);

  useEffect(() => {
    setSelectedOwner(externalSelectedOwner || []);
  }, [externalSelectedOwner]);

  useEffect(() => {
    setSelectedModified(externalSelectedModified || []);
  }, [externalSelectedModified]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      searchValue?.trim() ||
      selectedOwner?.length > 0 ||
      selectedModified?.length > 0
    );
  }, [searchValue, selectedOwner, selectedModified]);

  const handleNewKnowledgeBase = useCallback(() => {
    navigate("/v2/knowledge", {
      state: { openCreateModal: true },
    });
  }, [navigate]);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>
        <ButtonMenu
          key={`owner-filter-${filtersKey}`}
          label={getOwnerLabel()}
          menuPosition="bottom"
          onChange={handleOwnerChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
        >
          <ButtonMenu.Menu>
            {ownerOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>
        <ButtonMenu
          key={`modified-filter-${filtersKey}`}
          label={getModifiedLabel()}
          menuPosition="bottom"
          onChange={handleModifiedChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
        >
          <ButtonMenu.Menu>
            {modifiedOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>
        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>
      <FiltersRightContent>
        <SortMenu
          options={[
            { value: "name", label: "Name" },
            { value: "created", label: "Created" },
            { value: "modified", label: "Modified" },
          ]}
          selectedOption={selectedSortOption}
          orderDirection={orderDirection}
          onChange={onSortChange}
          onOrderChange={onOrderChange}
        />
        <Button
          iconRight="plus"
          label="New Knowledge Base"
          size="medium"
          onClick={handleNewKnowledgeBase}
          type="gradient"
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
